import _ from 'lodash';
import { Listbox, Transition } from '@headlessui/react';
import { CaretDown, CaretUp, X } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export type Option = {
  value: string;
  label?: string;
};

type AutoCompleteSelectBoxControlledProps = {
  fetchOptions: (query: string) => Promise<void>;
  defaultValue?: Option;
  value?: Option | null;
  variant?: 'outlined' | 'contained' | 'text';
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  direction?: 'up' | 'down';
  options: Option[];
  allowRemoveOption?: boolean;
  placeholder?: string;
};

const variantClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 hover:opacity-90',
    secondary: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
    default: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
  },
  outlined: {
    primary: 'border hover:border-primary hover:text-primary',
    secondary:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
    default:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
  },
  text: {
    primary: 'text-primary hover:text-primary-dark border-none',
    secondary: 'text-neutral-400 hover:text-neutral-500 border-none',
    default: 'text-neutral-400 hover:text-neutral-500 border-none',
  },
};

const activeClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 opacity-90',
    secondary: 'bg-neutral-500 text-neutral-0',
    default: 'bg-neutral-500 text-neutral-0',
  },
  outlined: {
    primary: 'border border-primary text-primary',
    secondary: 'border border-neutral-400 text-neutral-0 bg-neutral-400',
    default: 'border border-neutral-400 text-neutral-0 bg-neutral-400',
  },
  text: {
    primary: 'text-primary border-0 border-transparent',
    secondary: 'text-neutral-400 border-0 border-transparent',
    default: 'text-neutral-400 border-0 border-transparent',
  },
};

const sizeClasses = {
  small: 'px-2 py-1 text-sm leading-6',
  medium: 'px-3 py-2.5 text-base leading-6',
  large: 'px-4 py-3 text-lg leading-7',
};

export const AutoCompleteSelectBoxControlled = ({
  fetchOptions,
  defaultValue,
  value,
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  className = '',
  onChange,
  disabled = false,
  direction = 'down',
  options,
  allowRemoveOption = true,
  placeholder = '',
}: AutoCompleteSelectBoxControlledProps) => {
  // Use null instead of undefined for consistent controlled behavior
  const [selected, setSelected] = useState<Option | null>(
    value !== undefined ? value : defaultValue || null,
  );
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const maxHeight = 270; // Maximum height for dropdown (5 items * 54px)

  // Update internal state when value changes from parent
  useEffect(() => {
    // Only update if value is explicitly passed (even if null)
    if (value !== undefined) {
      setSelected(value);
    }
  }, [value]);

  // Store the latest fetchOptions in a ref so the debounced function always uses the current version.
  const fetchOptionsRef = useRef(fetchOptions);
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
  }, [fetchOptions]);

  const debouncedFetchOptions = useRef(
    _.debounce(async (newQuery) => {
      setLoading(true);
      try {
        // Use the current version of fetchOptions from the ref
        await fetchOptionsRef.current(newQuery);
      } catch (error) {
        console.error('Error fetching options:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
  );

  useEffect(() => {
    debouncedFetchOptions.current(query);
  }, [query]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const itemHeight = 54;
      const dropdownHeight =
        Math.min(options.length * itemHeight, maxHeight) + 10; // Add 10px to account for borders and padding
      const viewportHeight = window.innerHeight;
      const buttonHeight = rect.height;

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const padding = 8; // Padding is 4 on each side, totaling 8 for top and bottom

      // Check if dropdown would be cut off when going down
      const wouldBeCutOffDown =
        rect.top + buttonHeight + padding + dropdownHeight > viewportHeight;

      const shouldShowAbove =
        direction === 'up' ||
        (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) ||
        wouldBeCutOffDown;

      let top: number;

      const labelSize = 24;

      if (shouldShowAbove) {
        const calculatedTop =
          rect.top - dropdownHeight - labelSize + itemHeight;
        top = Math.max(padding, calculatedTop);
      } else {
        top = rect.top + buttonHeight + padding;
      }

      setPosition({
        top: top,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  const variantClass = variantClasses[variant][color];
  const sizeClass = sizeClasses[size];

  return (
    <Listbox
      value={selected}
      onChange={(value: Option) => {
        // Only update internal state if not fully controlled
        if (value === undefined) {
          setSelected(value);
        }
        onChange?.(value?.value || '');
      }}
      disabled={disabled || !options || options.length === 0}
    >
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            ref={buttonRef}
            onClick={updatePosition}
            className={clsx(
              'w-full rounded-md border border-neutral-200',
              open ? activeClasses[variant][color] : variantClass,
              `${variantClass} ${sizeClass}`,
              'flex justify-between items-center leading-6',
              'focus:outline-none',
              'bg-neutral-0',
              className,
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            <div className="flex-1 text-left truncate">
              {selected ? (
                selected.label || selected.value
              ) : (
                <i>
                  {(options ?? []).length > 0
                    ? placeholder || 'Select value...'
                    : 'No options available'}
                </i>
              )}
            </div>
            {direction === 'up' ? (
              <CaretUp className="ml-2 flex-shrink-0" />
            ) : (
              <CaretDown className="ml-2 flex-shrink-0" />
            )}
          </Listbox.Button>

          <Transition
            as={'div'}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {createPortal(
              <Listbox.Options
                ref={listboxRef}
                className={clsx(
                  'absolute z-[9999] bg-neutral-0 shadow-lg rounded-md py-1',
                  'focus:outline-none text-sm',
                  'border border-neutral-100',
                )}
                style={{
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  width: `${position.width}px`,
                  position: 'fixed',
                  maxHeight: `${maxHeight}px`,
                }}
              >
                <div className="flex justify-between items-center border-b border-neutral-100 sticky top-0 bg-neutral-0">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full px-3 py-2.5 focus:outline-none text-sm"
                  />
                  {query?.length > 0 && (
                    <button onClick={() => setQuery('')} className="px-3">
                      <X />
                    </button>
                  )}
                </div>

                <div className="overflow-auto block max-h-[200px]">
                  {loading ? (
                    <div className="text-center py-2.5 text-neutral-400">
                      Loading...
                    </div>
                  ) : (options ?? []).length > 0 ? (
                    options!.map((option) => (
                      <Listbox.Option
                        key={option.value}
                        value={option}
                        className={({ active }) =>
                          clsx(
                            'cursor-pointer select-none relative py-3 px-4 m-2 rounded-lg truncate',
                            active && !option?.value && 'bg-neutral-100',
                            option?.value === defaultValue?.value &&
                              'bg-neutral-100',
                            option?.value === defaultValue?.value &&
                              'font-bold',
                            'text-neutral-600',
                            'hover:bg-neutral-50',
                            'active:bg-neutral-100',
                          )
                        }
                      >
                        <div className="flex justify-between items-center">
                          <span className="truncate">
                            {option.label || option.value}
                          </span>
                          {selected?.value === option.value &&
                            allowRemoveOption && (
                              <X
                                className="w-4 h-4 text-neutral-400 hover:text-neutral-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle empty selection consistently
                                  setSelected(null);
                                  onChange?.('');
                                }}
                              />
                            )}
                        </div>
                      </Listbox.Option>
                    ))
                  ) : (
                    <div className="text-center py-2.5 text-neutral-400">
                      No options available
                    </div>
                  )}
                </div>
              </Listbox.Options>,
              document.body,
            )}
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
